<template>
  <form @submit.prevent="onSubmit">
    <h2>{{ t('partnerSubType') }}</h2>

    <div class="mt-4 grid grid-cols-3 gap-4">
      <InputField name="name.de" :label="t('nameDe')" class="col-start-1" />
      <InputField name="name.en" :label="t('nameEn')" />
      <SelectField name="role" :label="t('role')" :options="roleOptions" class="col-start-1" />
      <SelectField
        v-if="formValues.role === 'AGENCY'"
        name="type"
        :label="t('type')"
        :options="typeOptions"
      />
      <SelectField
        name="agencySettings.commissionPercent"
        :label="t('commissionPercent')"
        :options="commissionOptions"
        type="number"
      />
      <SelectField
        name="agencySettings.discountPercent"
        :label="t('discountPercent')"
        :options="discountOptions"
        type="number"
      />
    </div>

    <div class="mt-4">
      <CheckboxField name="agencySettings.noDeposit" :label="t('noDeposit')" />
    </div>

    <Divider />

    <div class="mt-3 flex justify-end">
      <CVButton variant="default" :is-loading="isLoading" @click="onSubmit">{{
        t('save')
      }}</CVButton>
    </div>
  </form>
</template>

<script lang="ts" setup>
import CVButton from '@/components/buttons/CVButton.vue';
import CheckboxField from '@/components/CheckboxField.vue';
import Divider from '@/components/Divider.vue';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import {
  AgencyType,
  type PartnerSubType,
  type UpdatePartnerSubTypeDto,
} from '@carvia/ros-client-types';
import { UserRole } from '@/entities/auth/user-role.enum';
import { useForm } from 'vee-validate';
import { cloneDeep } from 'lodash';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { partnerSubTypeSchema } from '@/validation/partner-sub-type.schema';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import { useYieldManagementSettings } from '@/queries/use-yield-management-settings';

const props = defineProps<{
  partnerSubType?: PartnerSubType;
  isLoading: boolean;
  isFetching: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: UpdatePartnerSubTypeDto): void;
}>();

const { t } = useI18n();
const { data: yieldSettings } = useYieldManagementSettings();

const initialValues: UpdatePartnerSubTypeDto = {
  name: props.partnerSubType?.name ?? { de: '', en: '' },
  role: props.partnerSubType?.role ?? 'AGENCY',
  type: props.partnerSubType?.type ?? null,
  agencySettings: props.partnerSubType?.agencySettings ?? {
    commissionPercent: 0,
    discountPercent: 0,
    noDeposit: false,
  },
};

const roleOptions = computed(() => [
  {
    label: t(`userRoles.${UserRole.AGENCY}`),
    value: UserRole.AGENCY,
  },
  {
    label: t(`userRoles.${UserRole.COMPANY}`),
    value: UserRole.COMPANY,
  },
]);

const typeOptions = computed(() => [
  {
    label: '-',
    value: null,
  },
  ...Object.values(AgencyType).map((type) => ({
    label: t(`agencyTypes.${type}`),
    value: type,
  })),
]);

const commissionOptions = computed(() => [
  {
    label: '-',
    value: 0,
  },
  ...(yieldSettings.value?.commissionPercentageOptions.map((option) => ({
    label: option.label,
    value: option.value,
  })) || []),
]);

const discountOptions = computed(() => [
  {
    label: '-',
    value: 0,
  },
  ...(yieldSettings.value?.discountPercentageOptions.map((option) => ({
    label: option.label,
    value: option.value,
  })) || []),
]);

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm({
  initialValues,
  validationSchema: partnerSubTypeSchema,
});

watch(
  () => formValues.role,
  (role) => {
    if (role !== 'AGENCY') {
      setFieldValue('type', null);
    }
  },
);

const unchangedValues = ref(cloneDeep(formValues));

const onSubmit = handleSubmit((values) => {
  unchangedValues.value = cloneDeep(formValues);
  emit('onSubmit', values);
});

usePreventLeavingUnsavedForm(() => formHasChanged(unchangedValues.value, formValues));
</script>

<i18n lang="json">
{
  "en": {
    "partnerSubType": "Partner Sub Type",
    "nameDe": "Name (DE)",
    "nameEn": "Name (EN)",
    "role": "Role",
    "type": "Type",
    "commissionPercent": "Commission Percent",
    "discountPercent": "Discount Percent",
    "noDeposit": "No Deposit",
    "save": "Save"
  },
  "de": {
    "partnerSubType": "Partner-Subtyp",
    "nameDe": "Name (DE)",
    "nameEn": "Name (EN)",
    "role": "Rolle",
    "type": "Typ",
    "commissionPercent": "Provisionsprozentsatz",
    "discountPercent": "Rabattprozentsatz",
    "noDeposit": "Keine Kaution",
    "save": "Speichern"
  }
}
</i18n>
